import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import { formatDate, tableSort, getTableComparator } from '../../utils/functions';
import CheckIcon from '@material-ui/icons/Check';
import {
  updateRecipients,
  removeRecipient,
  getFireHornSetting,
  sendTofireHornAPI,
  getPTSConnectSetting,
  sendToPTSConnectAPI,
} from '../../reducers/PagingReducer';
import { showSpinner, hideSpinner } from '../../reducers/UiReducer';
import { handleError } from '../../reducers/ErrorReducer';
import { setEditPageRecipient } from '../../reducers/DialogsReducer';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  table: {
    minWidth: 750,
    '& td': {
      paddingTop: 2,
      paddingBottom: 2,
    },
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `0 ${theme.spacing(2)}px`,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  actions: {
    '& button': {
      opacity: 0,
      transition: 'opacity 0.3s',
    },
  },
  row: {
    '&:hover': {
      '& $actions': {
        '& button': {
          opacity: 1,
        },
      },
    },
  },
}));

const headCells = [
  { id: 'Recipient', numeric: false, disablePadding: false, label: 'Recipient' },
  { id: 'Created', numeric: false, disablePadding: false, label: 'Created' },
  { id: 'CreatedBy', numeric: false, disablePadding: false, label: 'Created By' },
  { id: 'Updated', numeric: false, disablePadding: false, label: 'Updated' },
  { id: 'UpdatedBy', numeric: false, disablePadding: false, label: 'Updated By' },
];

function CustomTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;

  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.label === 'Recipient' ? "left" : "center"}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align="center">Active</TableCell>
        <TableCell align="center">FireHorn</TableCell>
        <TableCell align="center">Vocal</TableCell>
        <TableCell align="center">Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

function Recipients(props) {
  const classes = useStyles();
  const { recipients } = props;
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    props.updateRecipients();
    // eslint-disable-next-line
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, recipients.length - page * rowsPerPage);

  const addRecipient = () => {
    props.setEditPageRecipient({ ptsRecipientID: true, recipients: recipients });
  };

  const delRecipient = recipient => {
    if (!window.confirm(`Are you sure you want to remove recipient ${recipient.Recipient}?`))
      return;
    props.showSpinner();
    removeRecipient(recipient.ptsRecipientID)
      .then(async () => {
        // ================= FIREHORN =================
        if (recipient.IsFireHorn && recipient.FireHornUserId) {
          const settings = await getFireHornSetting();
          const fireHornSetting = settings?.find(
            s => s.agency?.toLowerCase() === recipient?.AgencyId?.toLowerCase()
          );
          if (fireHornSetting?.agencyId) {
            await sendTofireHornAPI(
              'DELETE',
              `users/${recipient.FireHornUserId}/agencies/${fireHornSetting.agencyId}/`,
              null
            );
          }
        }
        // ================= PTS CONNECT =================
        if (recipient.IsPTSConnect && recipient.ptsPersonID) {
          const settings = await getPTSConnectSetting();
          const ptsSetting = settings?.find(
            s => s.agency?.toLowerCase() === recipient?.AgencyId?.toLowerCase()
          );
          if (ptsSetting?.agencyId) {
            await sendToPTSConnectAPI(
              'POST',
              `user/delete/`,
              {'member_code': String(recipient.ptsPersonID)},
            );
          }
        }
        props.updateRecipients();
      })
      .catch(error => props.handleError(error, 'Error, recipient not deleted.'))
      .finally(() => props.hideSpinner());
  };

  const editRecipient = recipient => {
    props.setEditPageRecipient({
      ptsRecipientID: recipient.ptsRecipientID,
      recipients: recipients,
    });
  };

  const renderHeader = () => {
    return (
      <div className={classes.header}>
        <h4>Recipients</h4>
        <Fab size="small" color="secondary" onClick={addRecipient}>
          <AddIcon />
        </Fab>
      </div>
    );
  };

  const renderTable = () => {
    return (
      <>
        <TableContainer>
          <Table className={classes.table} aria-labelledby="tableTitle" size="small">
            <CustomTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={recipients.length}
            />
            <TableBody>
              {tableSort(recipients, getTableComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow hover key={row.ROWGUID} className={classes.row}>
                      <TableCell align="left">{row.Recipient}</TableCell>
                      <TableCell align="center">{formatDate(row.Created)}</TableCell>
                      <TableCell align="left">{row.CreatedBy}</TableCell>
                      <TableCell align="center">{formatDate(row.Updated)}</TableCell>
                      <TableCell align="center">{row.UpdatedBy}</TableCell>
                      <TableCell align="center">{row.IsDisabled ? '' : <CheckIcon />}</TableCell>
                      <TableCell align="center">{row.IsFireHorn ? <CheckIcon /> : ''}</TableCell>
                      <TableCell align="center">{row.IsPTSConnect ? <CheckIcon /> : ''}</TableCell>
                      <TableCell align="right" className={classes.actions}>
                        <IconButton onClick={() => editRecipient(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => delRecipient(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={recipients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </>
    );
  };

  return (
    <div className={classes.root}>
      {renderHeader()}
      {renderTable()}
    </div>
  );
}

const mapStateToProps = state => {
  return {
    recipients: state.paging.recipients,
  };
};

export default connect(mapStateToProps, {
  updateRecipients,
  showSpinner,
  hideSpinner,
  handleError,
  setEditPageRecipient,
})(Recipients);
